import type {
  SurveyDefinition,
  SurveyQuestion,
} from './survey-question.types'
import {
  parseSurveyQuestionRow,
  readString,
} from './parse-survey-question-fields'
import type { BranchingRule, QuestionRoutingRules } from '@/modules/routing'
import { isMultiSelectSurveyQuestion } from './choice-answer'
import { parseRoutingRulesFromDefinition } from './survey-routing-preview'

function destinationKey(rule: BranchingRule): string | null {
  if (rule.destination?.endOfInstrument === true) return 'end'
  const id = rule.destination?.destinationQuestionId?.trim() || rule.destinationQuestionId?.trim() || ''
  return id ? `q:${id}` : null
}

/** First is_equal_to rule wins: branching is applied before skip. */
function assignEqualToDestinations(
  out: Record<string, string>,
  rules: readonly BranchingRule[] | undefined,
): void {
  const sorted = [...(rules ?? [])].sort((a, b) => a.order - b.order)
  for (const rule of sorted) {
    if (rule.operator != null && rule.operator !== 'is_equal_to') continue
    const optionId = rule.valueOptionId?.trim()
    if (!optionId || out[optionId]) continue
    const key = destinationKey(rule)
    if (key) out[optionId] = key
  }
}

/**
 * On multi-select multiple choice, record each routed option's destination.
 * Options that share a destination can be selected together; different destinations cannot.
 * Checkbox is not routing-eligible (BFP-251) and keeps every selection.
 */
function withRoutingOptionDestinations(
  question: SurveyQuestion,
  routingRules: readonly QuestionRoutingRules[],
): SurveyQuestion {
  if (question.type !== 'multiple_choice' || !isMultiSelectSurveyQuestion(question)) return question
  const entry = routingRules.find((r) => r.questionId === question.id)
  if (!entry) return question
  const destinations: Record<string, string> = {}
  assignEqualToDestinations(destinations, entry.branchingRules)
  assignEqualToDestinations(destinations, entry.skipRules)
  return Object.keys(destinations).length
    ? { ...question, routingOptionDestinations: destinations }
    : question
}

export function parseSurveyDefinition(
  raw: unknown,
  fallbackInstrumentId: string,
  fallbackCampaignId: string,
): SurveyDefinition {
  if (raw == null || typeof raw !== 'object') {
    return {
      instrumentId: fallbackInstrumentId,
      campaignId: fallbackCampaignId,
      questions: [],
    }
  }
  const d = raw as Record<string, unknown>
  const instrumentId = readString(d, 'instrumentId') ?? fallbackInstrumentId
  const campaignId = readString(d, 'campaignId') ?? fallbackCampaignId
  const tenantId = readString(d, 'tenantId') ?? readString(d, 'tenant_id') ?? ''
  const tenantDomainPrefix =
    readString(d, 'tenantDomainPrefix') ??
    readString(d, 'tenant_domain_prefix') ??
    ''

  const routingRules = parseRoutingRulesFromDefinition(raw)
  const questions: SurveyQuestion[] = Array.isArray(d.questions)
    ? d.questions
        .map((row, i) => parseSurveyQuestionRow(row, i))
        .filter((q): q is SurveyQuestion => q != null)
        .map((q) => withRoutingOptionDestinations(q, routingRules))
        .sort((a, b) => a.order - b.order)
    : []

  return {
    instrumentId,
    campaignId,
    tenantId: tenantId || undefined,
    tenantDomainPrefix: tenantDomainPrefix || undefined,
    questions,
  }
}

export function findSurveyQuestion(
  definition: SurveyDefinition,
  questionId: string | null | undefined,
): SurveyQuestion | null {
  if (!questionId) return definition.questions[0] ?? null
  return definition.questions.find((q) => q.id === questionId) ?? null
}

export function questionIndexInDefinition(
  definition: SurveyDefinition,
  questionId: string,
): number {
  const idx = definition.questions.findIndex((q) => q.id === questionId)
  return Math.max(idx, 0)
}
